/**
 * KAN-722: блок "Метки" на вкладке Telegram-аккаунтов: префиксы меток (код,
 * коды телефонов, описание, число аккаунтов, следующая метка), добавление,
 * изменение и удаление (с подтверждением; ошибка сервера показывается текстом).
 */
import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { LabelPrefixForm } from '@/components/telegram/LabelPrefixForm';
import {
  useTelegramLabelPrefixMutations,
  useTelegramLabelPrefixes,
} from '@/hooks/useTelegramLabelPrefixes';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { LabelPrefixId, TelegramLabelPrefix } from '@/types/telegramLabelPrefix';

export const LABEL_PREFIXES_HELP =
  'Префиксы меток аккаунтов. При подключении префикс подбирается по коду телефона, метку с очередным номером (например UZ-17) назначает сервер.';
const EMPTY_TEXT = 'Префиксов нет. Нажмите "Добавить".';

type EditState = { mode: 'create' } | { mode: 'edit'; id: LabelPrefixId } | null;

function PrefixRow({
  prefix,
  onEdit,
  onDelete,
}: {
  prefix: TelegramLabelPrefix;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const id = String(prefix.id);
  return (
    <TableRow data-testid={`label-prefix-row-${id}`}>
      <TableCell className="text-sm font-medium">{prefix.code}</TableCell>
      <TableCell className="font-mono text-xs">{prefix.phone_prefixes.join(', ') || '-'}</TableCell>
      <TableCell className="max-w-[280px] whitespace-pre-wrap break-words text-xs text-gray-600">
        {prefix.description}
      </TableCell>
      <TableCell className="text-sm" data-testid={`label-prefix-row-count-${id}`}>
        {prefix.accounts_count}
      </TableCell>
      <TableCell className="text-sm" data-testid={`label-prefix-row-next-${id}`}>
        {prefix.next_label}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`label-prefix-row-edit-${id}`}
            onClick={onEdit}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Изменить
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs text-red-700"
            data-testid={`label-prefix-row-delete-${id}`}
            onClick={onDelete}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Удалить
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function LabelPrefixesBlock(): JSX.Element {
  const { data, isLoading, isError } = useTelegramLabelPrefixes();
  const { remove } = useTelegramLabelPrefixMutations();
  const prefixes = data ?? [];
  const [edit, setEdit] = useState<EditState>(null);
  const [toDelete, setToDelete] = useState<TelegramLabelPrefix | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const editing =
    edit?.mode === 'edit' ? prefixes.find((p) => p.id === edit.id) ?? null : null;

  const confirmDelete = (): void => {
    if (!toDelete) return;
    const prefix = toDelete;
    setToDelete(null);
    setDeleteError(null);
    remove.mutate(prefix.id, {
      onError: (err) =>
        setDeleteError(
          `${prefix.code}: ${extractErrorMessage(err, 'Не удалось удалить префикс')}`
        ),
    });
  };

  return (
    <section data-testid="label-prefixes-block" className="mt-6">
      <div className="mb-1 flex items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">Метки</h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-testid="label-prefix-add"
          onClick={() => setEdit({ mode: 'create' })}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Добавить
        </Button>
      </div>
      <p className="mb-3 text-xs text-gray-500">{LABEL_PREFIXES_HELP}</p>
      {edit && (edit.mode === 'create' || editing) && (
        <div className="mb-3">
          <LabelPrefixForm
            key={edit.mode === 'edit' ? String(edit.id) : 'create'}
            idPrefix="label-prefix-block"
            prefix={editing}
            onCancel={() => setEdit(null)}
            onSaved={() => setEdit(null)}
          />
        </div>
      )}
      {deleteError && (
        <p className="mb-2 text-xs text-red-700" data-testid="label-prefix-delete-error">
          {deleteError}
        </p>
      )}
      {isError && (
        <p className="text-sm text-red-700" data-testid="label-prefixes-error">
          Не удалось загрузить префиксы меток
        </p>
      )}
      {!isLoading && !isError && prefixes.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="label-prefixes-empty">
          {EMPTY_TEXT}
        </p>
      )}
      {prefixes.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <Table aria-label="Префиксы меток">
            <TableHeader>
              <TableRow>
                <TableHead>Префикс</TableHead>
                <TableHead>Коды телефонов</TableHead>
                <TableHead>Описание</TableHead>
                <TableHead>Аккаунтов</TableHead>
                <TableHead>Следующая метка</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {prefixes.map((prefix) => (
                <PrefixRow
                  key={String(prefix.id)}
                  prefix={prefix}
                  onEdit={() => setEdit({ mode: 'edit', id: prefix.id })}
                  onDelete={() => setToDelete(prefix)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        view={{
          title: 'Удалить префикс?',
          description: 'Префикс будет удалён из справочника. Метки аккаунтов не меняются.',
          confirm: 'Удалить',
        }}
        extra={toDelete ? `${toDelete.code}.` : null}
        onConfirm={confirmDelete}
        testId="label-prefix-delete-confirm"
      />
    </section>
  );
}

export default LabelPrefixesBlock;
